// `extension-alert-candidates-matching-summary` — "Matching summary", the first card in the
// highlights column of a profile opened FROM A SEARCH.
//
// ⚠️ IT RENDERS WHETHER OR NOT THERE IS A SUMMARY, and that is the whole reason it cannot be
// skipped: with nothing to show it still draws its header and the line "No summary available",
// 61 pixels of card that everything below it sits under. Leaving it out did not remove a card,
// it moved the ATS panel and both preview cards up the column — 36,493 differing pixels at 700,
// against 280 for the screen around it.
//
// ⚠️ AND ITS PLACE IN THE TREE IS A CONDITION, NOT A POSITION. The snippet renders it under
// `ng-if="ctrl.shortlistSessionInfo.viaHunter && !!ctrl.matchingSummary"` — so a profile opened
// by id shows no card at all, even when the server happens to answer with a summary, and the
// card is absent rather than empty there. `hasMatchingSummary` alone is not that condition.
import type { ReactElement } from 'react'

export interface MatchingSummaryCardProps {
  /** `ctrl.alertName` — the search's name. Absent, the "For … hunter" line does not render. */
  alertName?: string
  /**
   * `ctrl.summaryDisplay` — the sections built from `matchingSummary.matchingSummaryItems`.
   *
   * ⚠️ NOT PORTED, AND DELIBERATELY NOT GUESSED. Upstream's controller is 334 lines that group
   * the flat item list into sections, decide `maxItemsToShow` per section, mark which items are
   * `shown` before "Read more", and render six different item shapes (position, skill,
   * education, company type, industry, keywords) plus the ATS-filter ones. The dev tenant
   * answers with NO summary for every candidate in the reference alert — the card under test is
   * the empty one — so a port of that transform would be 300 lines no capture can check, which
   * is exactly the kind of confident-but-unverified work the baseline exists to prevent.
   *
   * Passing `true` renders the sections' container with nothing in it, which is wrong in a
   * visible way rather than a quiet one; until the transform lands, leave it unset and the card
   * says what it says on this tenant.
   */
  hasSummaryDisplay?: boolean
}

export function MatchingSummaryCard({ alertName, hasSummaryDisplay = false }: MatchingSummaryCardProps): ReactElement {
  return (
    <div className="alert-candidate-match-explanation">
      <div className="snippet-section">
        <div className="snippet-section-header">
          <div>
            <strong>Matching summary</strong>
          </div>
          {/* ⚠️ ONE LINE, BECAUSE THE TWO SPACES ARE CONTENT. "For ", the name in bold, then
              " hunter" — split across three JSX lines the spaces around <strong> vanish and the
              line reads "ForValidated emails onlyhunter". Fourth time in this port. */}
          {alertName === undefined || alertName === '' ? null : (
            <div className="section-header-extra-info truncate">For <strong>{alertName}</strong> hunter</div>
          )}
        </div>
        <div className="snippet-section-body">
          {hasSummaryDisplay ? <div className="summary-sections" /> : <div className="info">No summary available</div>}
        </div>
      </div>
    </div>
  )
}
